import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import feeDistAbi from '@/lib/abis/FeeDistributor.json'
import gaugeAbi from '@/lib/abis/GaugeController.json'
import { POLL_SLOW } from '@/lib/utils'

const notDeployed = (a) => !IS_LIVE || (a || '').toLowerCase() === ZERO_ADDR
// How many ENDED epochs back to scan for unclaimed fees. Claims are claim-once and per ended epoch,
// so an unclaimed old epoch stays claimable; a rolling window keeps the read bounded while covering
// far more history than a holder is likely to leave unclaimed. Genesis can sweep truly-stale epochs.
const EPOCH_WINDOW = 12

/// Fees-follow-votes claims (FeeDistributor): a vault's 80% fee remainder goes to the veNFT holders
/// who VOTED for its gauge that epoch, pro-rata. This scans the KNOWN vault set across the last
/// `EPOCH_WINDOW` ENDED epochs - claims are gated to ended epochs (the tally must be frozen), and the
/// vault's own token0/token1 identify the two amounts, so no event history is needed to discover
/// them. `claimable(vault, epoch, user)` returns the pair; rows with anything owed are grouped per
/// vault with the exact ended epochs to pass to `claim`.
export function useFeeClaims(user, vaults) {
  const fd = ADDRESSES.FEE_DISTRIBUTOR
  const gc = ADDRESSES.GAUGE_CONTROLLER
  const enabled = !notDeployed(fd) && !notDeployed(gc) && !!user

  const { data: epochRead, refetch: refetchEpoch } = useReadContracts({
    query: { enabled, refetchInterval: POLL_SLOW },
    contracts: [{ address: gc, abi: gaugeAbi, functionName: 'currentEpoch' }],
  })
  const cur = Number(epochRead?.[0]?.result ?? 0n)
  const endedEpochs = useMemo(
    () => (cur > 0 ? Array.from({ length: EPOCH_WINDOW }, (_, k) => cur - 1 - k).filter((e) => e >= 0) : []),
    [cur],
  )

  const vaultList = vaults ?? []
  const { data: reads, refetch: refetchClaimable } = useReadContracts({
    query: { enabled: enabled && vaultList.length > 0 && endedEpochs.length > 0, refetchInterval: POLL_SLOW },
    contracts: vaultList.flatMap((v) =>
      endedEpochs.map((e) => ({ address: fd, abi: feeDistAbi, functionName: 'claimable', args: [v.address, BigInt(e), user] })),
    ),
  })

  const groups = useMemo(() => {
    const w = endedEpochs.length
    return vaultList.map((v, vi) => {
      const perEpoch = endedEpochs.map((e, ei) => {
        const r = reads?.[vi * w + ei]?.result
        return { epoch: e, a0: r?.[0] ?? 0n, a1: r?.[1] ?? 0n }
      }).filter((x) => x.a0 > 0n || x.a1 > 0n)
      const total0 = perEpoch.reduce((a, x) => a + x.a0, 0n)
      const total1 = perEpoch.reduce((a, x) => a + x.a1, 0n)
      return { vault: v, total0, total1, epochs: perEpoch.map((x) => BigInt(x.epoch)) }
    }).filter((g) => g.epochs.length > 0)
  }, [reads, vaultList, endedEpochs])

  const refetch = async () => {
    await Promise.all([refetchEpoch(), refetchClaimable()].map((p) => p?.catch?.(() => {}) ?? p))
  }

  return { groups, hasAny: groups.length > 0, notDeployed: !enabled, refetch }
}
